import React, { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiEye,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiCheck,
  FiX,
  FiClock,
  FiDroplet,
  FiTrendingUp,
  FiChevronRight,
  FiUser,
  FiCalendar,
  FiMapPin,
  FiSearch,
  FiClipboard,
  FiSave,
} from "react-icons/fi";
import toast from "react-hot-toast";
import LoadingSpinner from "../Common/LoadingSpinner";
import ModalOverlay from "../Common/ModalOverlay";
import ConfirmModal from "../Common/ConfirmModal";
import BulkActionBar from "../Common/BulkActionBar";
import { usePlongees } from "../../hooks/Plongee/usePlongees";
import { useAdherents } from "../../hooks/Adherent/useAdherents";
import { useMoniteurs } from "../../hooks/Moniteur/useMoniteurs";
import { useSorties } from "../../hooks/Sortie/useSorties";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../hooks/useSelection";
import { formatDate } from "../../utils/helpers";
import { photoUrl } from "../../utils/photoUrl";
import {
  TYPE_PLONGEE_OPTIONS,
  VISIBILITE_OPTIONS,
  COURANT_OPTIONS,
} from "../../utils/constants";
import plongeeService from "../../services/Plongee/plongeeService";

import ErrorState from "../Common/ErrorState";

// Brouillon : plongée créée automatiquement au pointage de présence (voir
// PalanqueeService.creerPlongeeBrouillon), profondeur/durée pas encore
// saisies par le moniteur — distinct de "Non validée" (des mesures peuvent
// être renseignées sans que la plongée soit encore validée).
const isBrouillon = (p) => p.profondeur_max == null || p.duree == null;

const PlongeeList = () => {
  // TOUS LES HOOKS EN PREMIER - AVANT TOUT RETURN CONDITIONNEL
  const { user, hasRole } = useAuth();
  const canManagePlongee = hasRole(["president", "moniteur"]);
  const isMoniteur = hasRole(["moniteur"]);
  const [searchParams] = useSearchParams();
  const { useGetAll, useRemove, useValidate, useUpdate } = usePlongees();
  const { useGetAll: useGetAllAdherents } = useAdherents();
  const { useGetAll: useGetAllMoniteurs } = useMoniteurs();
  const { useGetAll: useGetAllSorties } = useSorties();

  const { data, isLoading, error, refetch } = useGetAll();
  const { data: adherentsData, isLoading: loadingAdherents } =
    useGetAllAdherents();
  // Uniquement pour le sous-menu "Mes plongées" (voir Sidebar.jsx) —
  // résoudre le moniteur connecté, et savoir quelles sorties il encadre
  // (Sortie.encadrants), pour couvrir aussi les plongées qu'il n'a pas
  // encore personnellement validées.
  const { data: moniteursData } = useGetAllMoniteurs({}, { enabled: isMoniteur });
  const { data: sortiesData } = useGetAllSorties({}, { enabled: isMoniteur });

  const remove = useRemove();
  const validate = useValidate();
  const update = useUpdate();
  const selection = useSelection();

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleteModal, setDeleteModal] = useState(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // Sous-menu sidebar "Mes plongées" (?filtre=mes-plongees, réservé au
  // moniteur, voir Sidebar.jsx) : plongées qu'il a validées OU issues d'une
  // sortie qu'il encadre (même s'il ne les a pas encore validées lui-même).
  const [mesPlongeesOnly, setMesPlongeesOnly] = useState(
    searchParams.get("filtre") === "mes-plongees",
  );
  useEffect(() => {
    setMesPlongeesOnly(searchParams.get("filtre") === "mes-plongees");
    setCurrentPage(1);
  }, [searchParams]);
  const itemsPerPage = 10;

  const currentMoniteurId = useMemo(() => {
    if (!user || !moniteursData?.data) return null;
    const moniteur = moniteursData.data.find((m) => m.user?.email === user.email);
    return moniteur?.id_moniteur || null;
  }, [user, moniteursData]);

  // Map id_sortie -> liste d'id_moniteur encadrants (Sortie.encadrants)
  const sortieEncadrantsMap = useMemo(() => {
    const map = new Map();
    (sortiesData?.data || []).forEach((s) => {
      map.set(s.id_sortie || s.id, Array.isArray(s.encadrants) ? s.encadrants : []);
    });
    return map;
  }, [sortiesData]);

  // Complétion rapide d'un brouillon (voir isBrouillon) : uniquement les
  // champs qu'un moniteur saisit après-coup — adhérent/sortie/date/type sont
  // déjà fixés depuis le pointage de présence, inutile de repasser par le
  // formulaire complet (PlongeeForm) pour eux.
  const [completeModal, setCompleteModal] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    profondeur_max: "",
    duree: "",
    temperature_eau: "",
    visibilite: "Bonne",
    courant: "Nul",
  });
  const [completeErrors, setCompleteErrors] = useState({});

  // Map des adhérents avec leurs infos (useMemo)
  const adherentMap = useMemo(() => {
    const map = {};
    if (adherentsData?.data) {
      adherentsData.data.forEach((adherent) => {
        map[adherent.num_adherent] = {
          nom: `${adherent.civilite} ${adherent.nom} ${adherent.prenom}`,
          photo: adherent.photo,
          num_adherent: adherent.num_adherent,
        };
      });
    }
    return map;
  }, [adherentsData]);

  const allPlongees = data?.data || [];

  // Priorité d'affichage : à compléter (brouillon) puis en attente (non
  // validée mais complète) en tête de liste, validées en dernier — ce sont
  // les deux seules qui demandent encore une action du moniteur.
  const priorite = (p) => {
    if (isBrouillon(p)) return 0;
    if (!p.id_moniteur_validateur) return 1;
    return 2;
  };

  // Filtrage (useMemo)
  const filteredPlongees = useMemo(() => {
    return allPlongees
      .filter((p) => {
      const adherentInfo = adherentMap[p.num_adherent] || {
        nom: `#${p.num_adherent}`,
        photo: null,
      };
      const adherentName = adherentInfo.nom;

      if (filter !== "all") {
        const isValide = !!p.id_moniteur_validateur;
        if (filter === "valide" && !isValide) return false;
        if (filter === "non_valide" && isValide) return false;
        if (filter === "brouillon" && !isBrouillon(p)) return false;
      }

      if (typeFilter !== "all" && p.type_plongee !== typeFilter) return false;

      if (mesPlongeesOnly) {
        const jeSuisValidateur =
          currentMoniteurId != null && p.id_moniteur_validateur === currentMoniteurId;
        const jEncadreLaSortie =
          currentMoniteurId != null &&
          (sortieEncadrantsMap.get(p.id_sortie) || []).includes(currentMoniteurId);
        if (!jeSuisValidateur && !jEncadreLaSortie) return false;
      }

      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          p.type_plongee?.toLowerCase().includes(search) ||
          adherentName.toLowerCase().includes(search)
        );
      }
      return true;
    })
      .sort((a, b) => priorite(a) - priorite(b));
  }, [
    allPlongees,
    adherentMap,
    filter,
    typeFilter,
    searchTerm,
    mesPlongeesOnly,
    currentMoniteurId,
    sortieEncadrantsMap,
  ]);

  const brouillonsCount = useMemo(
    () => allPlongees.filter(isBrouillon).length,
    [allPlongees],
  );

  // Pagination
  const totalPages = Math.ceil(filteredPlongees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPlongees = filteredPlongees.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Fonctions
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await remove.mutateAsync(id);
      refetch();
      setDeleteModal(null);
    } catch (error) {
      console.error("Échec de la suppression de la plongée :", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const result = await plongeeService.bulkDelete(Array.from(selection.selectedIds));
      toast[result.success ? "success" : "error"](result.message);
      selection.clear();
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur lors de la suppression groupée");
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteModal(false);
    }
  };

  const handleValidate = async (id) => {
    try {
      setLoading(true);
      await validate.mutateAsync(id);
      refetch();
    } catch (error) {
      console.error("Échec de la validation de la plongée :", error);
    } finally {
      setLoading(false);
    }
  };

  const openCompleteModal = (plongee) => {
    setCompleteForm({
      profondeur_max: plongee.profondeur_max ?? "",
      duree: plongee.duree ?? "",
      temperature_eau: plongee.temperature_eau ?? "",
      visibilite: plongee.visibilite || "Bonne",
      courant: plongee.courant || "Nul",
    });
    setCompleteErrors({});
    setCompleteModal(plongee.id_plongee);
  };

  const handleCompleteChange = (e) => {
    const { name, value } = e.target;
    setCompleteForm((prev) => ({ ...prev, [name]: value }));
    if (completeErrors[name]) setCompleteErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateCompleteForm = () => {
    const newErrors = {};
    if (!completeForm.profondeur_max || completeForm.profondeur_max <= 0)
      newErrors.profondeur_max = "La profondeur est requise";
    if (!completeForm.duree || completeForm.duree <= 0)
      newErrors.duree = "La durée est requise";
    setCompleteErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    if (!validateCompleteForm()) return;
    try {
      setLoading(true);
      await update.mutateAsync({ id: completeModal, data: completeForm });
      setCompleteModal(null);
      refetch();
    } catch (error) {
      console.error("Échec de la complétion de la plongée :", error);
    } finally {
      setLoading(false);
    }
  };

  // RETOURS CONDITIONNELS APRÈS TOUS LES HOOKS
  if (isLoading || loadingAdherents) return <LoadingSpinner variant="list" />;
  if (error) return <ErrorState onRetry={refetch} />;

  if (filteredPlongees.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-lg"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
          <FiDroplet className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {mesPlongeesOnly
            ? "Aucune plongée liée à vous"
            : searchTerm || filter !== "all"
              ? "Aucune plongée trouvée"
              : "Aucune plongée enregistrée"}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          {mesPlongeesOnly
            ? "Aucune plongée que vous avez validée ou issue d'une sortie que vous encadrez"
            : searchTerm || filter !== "all"
              ? "Aucun résultat pour vos critères"
              : "Commencez par enregistrer votre première plongée"}
        </p>
        {(searchTerm || filter !== "all" || typeFilter !== "all" || mesPlongeesOnly) && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm("");
              setFilter("all");
              setTypeFilter("all");
              setMesPlongeesOnly(false);
              setCurrentPage(1);
            }}
            className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <FiX className="w-4 h-4" /> Réinitialiser la recherche
          </button>
        )}
        {canManagePlongee && (
          <Link
            to="/plongees/create"
            className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            <FiPlus className="w-4 h-4" /> Nouvelle plongée
          </Link>
        )}
      </motion.div>
    );
  }

  // RENDU DU COMPOSANT
  return (
    <div className="space-y-4">
      {mesPlongeesOnly && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <FiUser className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
          {filteredPlongees.length} plongée
          {filteredPlongees.length > 1 ? "s" : ""} que vous avez validée(s) ou
          issue(s) d&apos;une sortie que vous encadrez
        </p>
      )}
      {/* Barre de recherche et filtres */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par adhérent ou type de plongée..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-900 focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-auto"
          >
            <option value="all">Toutes</option>
            <option value="brouillon">
              À compléter{brouillonsCount > 0 ? ` (${brouillonsCount})` : ""}
            </option>
            <option value="valide">Validées</option>
            <option value="non_valide">Non validées</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-900 focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-auto"
          >
            <option value="all">Tous les types</option>
            {TYPE_PLONGEE_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          {canManagePlongee && (
            <Link
              to="/plongees/create"
              className="col-span-2 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 sm:col-auto sm:w-auto"
            >
              <FiPlus className="w-4 h-4" />
              Nouvelle
            </Link>
          )}
        </div>
      </div>

      {/* Sélection multiple / actions groupées (mêmes plongées verrouillées
          que le bouton Supprimer individuel : déjà validées exclues) */}
      {canManagePlongee && (() => {
        const selectableIds = paginatedPlongees
          .filter((p) => !p.id_moniteur_validateur)
          .map((p) => p.id_plongee);
        return (
          <>
            {selectableIds.length > 0 && (
              <div className="flex items-center gap-2 px-1">
                <input
                  type="checkbox"
                  checked={selection.allSelected(selectableIds)}
                  onChange={() => selection.toggleAll(selectableIds)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Tout sélectionner (page courante)
                </span>
              </div>
            )}
            <BulkActionBar
              count={selection.selectedCount}
              onClear={selection.clear}
              onDelete={() => setBulkDeleteModal(true)}
            />
          </>
        );
      })()}

      {/* Liste des plongées */}
      <AnimatePresence>
        {paginatedPlongees.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl"
          >
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              Aucune plongée trouvée
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {searchTerm || filter !== "all"
                ? "Essayez de modifier vos filtres"
                : "Aucune plongée pour le moment"}
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid gap-3"
          >
            {paginatedPlongees.map((plongee) => {
              const adherentInfo = adherentMap[plongee.num_adherent] || {
                nom: `#${plongee.num_adherent}`,
                photo: null,
              };
              const adherentName = adherentInfo.nom;

              return (
                <motion.div
                  key={plongee.id_plongee}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    {canManagePlongee && !plongee.id_moniteur_validateur && (
                      <input
                        type="checkbox"
                        checked={selection.isSelected(plongee.id_plongee)}
                        onChange={() => selection.toggle(plongee.id_plongee)}
                        className="mt-1 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
                      />
                    )}
                    {/* Photo / Avatar de l'adhérent */}
                    <div className="flex-shrink-0">
                      {adherentInfo.photo ? (
                        <img
                          src={photoUrl(adherentInfo.photo)}
                          alt={adherentName}
                          className="w-16 h-16 rounded-full object-cover border-2 border-indigo-200 dark:border-indigo-700 shadow-sm"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 dark:from-indigo-900/30 dark:to-blue-900/30 flex items-center justify-center border-2 border-indigo-200 dark:border-indigo-700 shadow-sm">
                          <FiDroplet className="w-8 h-8 text-indigo-500 dark:text-indigo-400" />
                        </div>
                      )}
                      <div className="text-center mt-1">
                        <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
                          #{plongee.num_adherent}
                        </span>
                      </div>
                    </div>

                    {/* Informations */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900 dark:text-white">
                              {adherentName}
                            </h3>
                            <span className="text-sm text-gray-400 dark:text-gray-500">
                              •
                            </span>
                            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                              {plongee.type_plongee}
                            </span>
                            {isBrouillon(plongee) && (
                              <span
                                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400"
                                title="Créée automatiquement au pointage de présence, en attente de saisie par le moniteur"
                              >
                                <FiEdit className="w-3 h-3" /> À compléter
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <FiCalendar className="w-3.5 h-3.5 flex-shrink-0" />
                              {formatDate(plongee.date)}
                            </span>
                            <span className="flex flex-wrap items-center gap-3">
                              <span className="flex items-center gap-1">
                                Profondeur:{" "}
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                  {plongee.profondeur_max != null ? `${plongee.profondeur_max}m` : "—"}
                                </span>
                              </span>
                              <span className="flex items-center gap-1">
                                Durée:{" "}
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                  {plongee.duree != null ? `${plongee.duree}min` : "—"}
                                </span>
                              </span>
                            </span>
                            {plongee.id_moniteur_validateur ? (
                              <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                                <FiCheck className="w-3.5 h-3.5" /> Validée
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400 font-medium">
                                <FiClock className="w-3.5 h-3.5" /> En attente
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {/* Compléter - action mise en avant tant que la
                              plongée est un brouillon (voir isBrouillon) :
                              évite de passer par le formulaire complet pour
                              seulement saisir profondeur/durée/etc. */}
                          {canManagePlongee && isBrouillon(plongee) && (
                            <button
                              onClick={() => openCompleteModal(plongee)}
                              disabled={loading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded-lg transition-colors disabled:opacity-50"
                              title="Compléter les données de cette plongée"
                            >
                              <FiClipboard className="w-4 h-4" /> Compléter
                            </button>
                          )}
                          {/* Valider - uniquement si non validée ; désactivé
                              tant que profondeur/durée ne sont pas saisies
                              (voir PlongeeService.validatePlongee, même
                              règle revalidée côté serveur). */}
                          {canManagePlongee && !plongee.id_moniteur_validateur && (
                            <button
                              onClick={() => handleValidate(plongee.id_plongee)}
                              disabled={loading || isBrouillon(plongee)}
                              className="p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={
                                isBrouillon(plongee)
                                  ? "Complétez d'abord la profondeur et la durée"
                                  : "Valider la plongée"
                              }
                            >
                              <FiCheck className="w-4 h-4" />
                            </button>
                          )}

                          <Link
                            to={`/plongees/${plongee.id_plongee}`}
                            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title="Voir"
                          >
                            <FiEye className="w-4 h-4" />
                          </Link>
                          {/* Verrouillé côté serveur (PlongeeService.update/delete) :
                              une plongée validée par un moniteur n'est plus
                              modifiable ni supprimable. */}
                          {canManagePlongee && !plongee.id_moniteur_validateur && (
                            <>
                              <Link
                                to={`/plongees/edit/${plongee.id_plongee}`}
                                className="p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                                title="Modifier"
                              >
                                <FiEdit className="w-4 h-4" />
                              </Link>
                              <button
                                onClick={() => setDeleteModal(plongee.id_plongee)}
                                disabled={loading}
                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                                title="Supprimer"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700"
        >
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>
          <span className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
          </button>
        </motion.div>
      )}

      {/* Modal de confirmation de suppression */}
      {deleteModal && (
        <ModalOverlay className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full shadow-2xl"
          >
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400 mb-4">
              <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/30">
                <FiTrash2 className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold">Confirmer la suppression</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Êtes-vous sûr de vouloir supprimer cette plongée ?
              <br />
              <span className="text-sm text-red-500 font-medium">
                Cette action est irréversible.
              </span>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(deleteModal)}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
              >
                Supprimer
              </button>
            </div>
          </motion.div>
        </ModalOverlay>
      )}

      {/* Modal de complétion rapide d'un brouillon — uniquement les champs
          qu'un moniteur saisit après le pointage (voir openCompleteModal),
          pas le formulaire complet (PlongeeForm) qui redemanderait
          adhérent/sortie/date/type déjà fixés. */}
      {completeModal && (
        <ModalOverlay className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.form
            onSubmit={handleCompleteSubmit}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-md w-full shadow-2xl"
          >
            <div className="flex items-center gap-3 text-amber-600 dark:text-amber-400 mb-4">
              <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                <FiClipboard className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Compléter la plongée
              </h3>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Profondeur max (m) *
                  </label>
                  <input
                    type="number"
                    name="profondeur_max"
                    value={completeForm.profondeur_max}
                    onChange={handleCompleteChange}
                    min="0"
                    placeholder="0"
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      completeErrors.profondeur_max
                        ? "border-red-400"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                  />
                  {completeErrors.profondeur_max && (
                    <p className="mt-1 text-xs text-red-500">
                      {completeErrors.profondeur_max}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Durée (min) *
                  </label>
                  <input
                    type="number"
                    name="duree"
                    value={completeForm.duree}
                    onChange={handleCompleteChange}
                    min="0"
                    placeholder="0"
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      completeErrors.duree
                        ? "border-red-400"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                  />
                  {completeErrors.duree && (
                    <p className="mt-1 text-xs text-red-500">{completeErrors.duree}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Température de l&apos;eau (°C)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="temperature_eau"
                  value={completeForm.temperature_eau}
                  onChange={handleCompleteChange}
                  placeholder="Facultatif"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Visibilité
                  </label>
                  <select
                    name="visibilite"
                    value={completeForm.visibilite}
                    onChange={handleCompleteChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {VISIBILITE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Courant
                  </label>
                  <select
                    name="courant"
                    value={completeForm.courant}
                    onChange={handleCompleteChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {COURANT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setCompleteModal(null)}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors disabled:opacity-60"
              >
                <FiSave className="w-4 h-4" />
                Enregistrer
              </button>
            </div>
          </motion.form>
        </ModalOverlay>
      )}

      <ConfirmModal
        isOpen={bulkDeleteModal}
        message={`Êtes-vous sûr de vouloir supprimer les ${selection.selectedCount} plongée(s) sélectionnée(s) ?`}
        confirmLabel={isBulkDeleting ? "Suppression..." : "Supprimer"}
        onCancel={() => setBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  );
};

export default PlongeeList;
